import { supabase } from "@/libs/supabase";
import type { ProgressLog, DbProgressLog } from "@/types/progressLog";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

// データベースの型からアプリケーションの型への変換関数
export const convertToAppProgressLog = (
	dbProgressLog: DbProgressLog,
): ProgressLog => ({
	id: dbProgressLog.id,
	goalId: dbProgressLog.goal_id,
	userId: dbProgressLog.user_id,
	progress: dbProgressLog.progress,
	note: dbProgressLog.note || undefined,
	createdAt: dbProgressLog.created_at,
});

export const progressLogService = {
	// 進捗記録一覧の取得
	async getProgressLogs(goalId?: string) {
		let query = supabase
			.from("progress_logs")
			.select("*")
			.order("created_at", { ascending: false });

		if (goalId) {
			query = query.eq("goal_id", goalId);
		}

		const { data, error } = await query;
		if (error) throw error;

		return (data as unknown as DbProgressLog[]).map(convertToAppProgressLog);
	},

	// 特定の進捗記録の取得
	async getProgressLogById(id: string) {
		const { data, error } = await supabase
			.from("progress_logs")
			.select("*")
			.eq("id", id)
			.single();

		if (error) throw error;
		return convertToAppProgressLog(data as unknown as DbProgressLog);
	},

	// 進捗記録の作成
	async createProgressLog(
		progressLog: Omit<ProgressLog, "id" | "userId" | "createdAt">,
	) {
		const { data, error } = await supabase
			.from("progress_logs")
			.insert([
				{
					goal_id: progressLog.goalId,
					progress: progressLog.progress,
					note: progressLog.note,
					user_id: (await supabase.auth.getUser()).data.user?.id,
				},
			])
			.select()
			.single();

		if (error) throw error;
		return convertToAppProgressLog(data as unknown as DbProgressLog);
	},

	// 進捗記録の更新
	async updateProgressLog(
		id: string,
		progressLog: Partial<Omit<ProgressLog, "id" | "userId" | "createdAt">>,
	) {
		const updateData: Partial<DbProgressLog> = {
			progress: progressLog.progress,
			note: progressLog.note || null,
		};

		const { data, error } = await supabase
			.from("progress_logs")
			.update(updateData)
			.eq("id", id)
			.select()
			.single();

		if (error) throw error;
		return convertToAppProgressLog(data as unknown as DbProgressLog);
	},

	// 進捗記録の削除
	async deleteProgressLog(id: string) {
		const { error } = await supabase
			.from("progress_logs")
			.delete()
			.eq("id", id);

		if (error) throw error;
	},

	// リアルタイム更新のサブスクリプション
	subscribeToChanges(
		callback: (payload: RealtimePostgresChangesPayload<DbProgressLog>) => void,
		goalId?: string,
	) {
		const channel = supabase.channel("progress_log_changes");

		if (goalId) {
			return channel
				.on(
					"postgres_changes",
					{
						event: "*",
						schema: "public",
						table: "progress_logs",
						filter: `goal_id=eq.${goalId}`,
					},
					callback,
				)
				.subscribe();
		}

		return channel
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "progress_logs",
				},
				callback,
			)
			.subscribe();
	},
};
